import { SceneService } from '@src/services/SceneService';

import { VideoTranscriptElement } from '../models/elements/VideoTranscriptElement';
import { EditorChangeType } from '../types';
import { AbstractCommand } from './AbstractCommand';
import { TimelineMode } from './TimelineMode';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';
import type { DeleteInfo } from '@src/services/SceneService';

/**
 * Command to remove an element from the timeline.
 * Supports both transcript and regular elements with proper reconciliation.
 */
export class RemoveElementCommand extends AbstractCommand {
  private readonly sceneService = new SceneService();

  private elementId: string;
  private removedElement?: Element;
  private trackId?: string;
  private elementRemoved: boolean = false;
  private deleteInfo: DeleteInfo | null = null;
  private _shouldCreateUndoRedoEntry: boolean;
  private mode: TimelineMode;

  getName(): string {
    return 'RemoveElementCommand';
  }

  shouldCreateUndoRedoEntry(): boolean {
    return this._shouldCreateUndoRedoEntry;
  }

  constructor(
    editor: VideoEditor,
    elementId: string,
    createUndoRedoEntry: boolean = true,
    mode: TimelineMode = TimelineMode.DEFAULT,
  ) {
    const element = editor.getElementById(elementId);
    super(editor, element);
    this.elementId = elementId;
    this.removedElement = element;
    this.trackId = this.editor.timeline.getTrackIdForElement(elementId);
    this._shouldCreateUndoRedoEntry = createUndoRedoEntry;
    this.mode = mode;
  }

  execute(): boolean {
    if (!this.removedElement || !this.trackId) return false;

    // Capture deleteInfo before removing element
    this.captureDeleteInfoForScenesAdjustment();

    let success: boolean;
    if (this.removedElement instanceof VideoTranscriptElement) {
      success = this.editor
        .getTranscriptTrackManager()
        .removeElement(this.removedElement, this.editor.getTimelineMode());
    } else {
      // Use TrackManager.removeElement which supports ripple modes
      success = this.editor
        .getTrackManager()
        .removeElement(this.removedElement, this.mode);
    }

    if (success) {
      this.elementRemoved = true;

      // Adjust scenes using captured deleteInfo
      if (this.deleteInfo) {
        this.sceneService.adjustScenesAfterDelete(
          this.editor.timeline,
          this.deleteInfo,
        );
      }

      // Recalculate timeline and reconcile dependent tracks
      this.recalculateTimeline();

      this.editor.markDirty('remove_element_command');
    }
    return success;
  }

  undo(): boolean {
    if (!this.elementRemoved || !this.removedElement || !this.trackId)
      return false;

    const success = this.editor.timeline.addElement(
      this.trackId,
      this.removedElement,
    );
    if (success) {
      this.elementRemoved = false;

      // Recalculate timeline and reconcile dependent tracks
      this.recalculateTimeline();

      this.editor.markDirty('remove_element_command_undo');
      this.editor.notifyObservers(EditorChangeType.ELEMENT_ADDED, {
        element: this.removedElement,
        trackId: this.trackId,
      });
    }
    return success;
  }

  redo(): boolean {
    if (this.elementRemoved) return false;
    return this.execute();
  }

  getDescription(): string {
    const description = this.removedElement
      ? `${this.removedElement.type} element`
      : `element ${this.elementId}`;
    return `Remove ${description}`;
  }

  /**
   * Capture information about adjacent elements before deletion.
   * This follows the pattern used in TranscriptRemoveCommand and ShiftElementCommand.
   */
  private captureDeleteInfoForScenesAdjustment(): void {
    if (!this.removedElement || !this.trackId) {
      return;
    }

    const track = this.editor.timeline.getTrack(this.trackId);
    if (!track) {
      return;
    }

    const elements = [...track.elements];
    const elementIndex = elements.findIndex(el => el.id === this.elementId);
    if (elementIndex === -1) {
      return;
    }

    const previousElement = elements[elementIndex - 1];
    const nextElement = elements[elementIndex + 1];

    this.deleteInfo = {
      deletedElementId: this.elementId,
      previousElementId: previousElement?.id ?? null,
      previousElementMediaEnd:
        this.getElementMediaEndTime(previousElement) ?? null,
      nextElementId: nextElement?.id ?? null,
      nextElementMediaStart: this.getElementMediaStartTime(nextElement) ?? null,
    };
  }

  /**
   * Gets the media start time for an element.
   * If the element has a getMediaStartTime method, use it; otherwise return null.
   */
  private getElementMediaStartTime(
    element: Element | undefined,
  ): number | null {
    if (!element) return null;
    if (
      'getMediaStartTime' in element &&
      typeof element.getMediaStartTime === 'function'
    ) {
      return element.getMediaStartTime();
    }
    return null;
  }

  /**
   * Gets the media end time for an element.
   * If the element has a getMediaEndTime method, use it; otherwise return null.
   */
  private getElementMediaEndTime(element: Element | undefined): number | null {
    if (!element) return null;
    if (
      'getMediaEndTime' in element &&
      typeof element.getMediaEndTime === 'function'
    ) {
      return element.getMediaEndTime();
    }
    return null;
  }
}
